import { useAnalytics } from '@contexts/AnalyticsContext'
import { ProductType } from '@models/Product'
import { UserKind } from '@models/user/UserAnalyticsGraph'
import { VenueKind } from '@models/venue/VenueAnalyticsGraph'
import { useEffect, useState } from 'react'

interface BaseFetchParams {
  entity: 'user' | 'venue'
  entityId?: number
  kind: VenueKind | UserKind
  startDate: string
  endDate: string
  product?: ProductType
}

interface UseFetchChartDataProps<TParams extends BaseFetchParams, TResponse> {
  entityId?: number
  kind: VenueKind | UserKind
  entity: 'user' | 'venue'
  fetchFunction: (params: TParams) => Promise<{ data: TResponse }>
  dependencies?: any[]
}

function useFetchChartData<TParams extends BaseFetchParams, TResponse>({
  entityId,
  kind,
  entity,
  fetchFunction,
  dependencies = [],
}: UseFetchChartDataProps<TParams, TResponse>) {
  const { selectedDates, selectedProduct } = useAnalytics()

  const [data, setData] = useState<TResponse | undefined>()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | undefined>()

  const fetchData = async () => {
    setIsLoading(true)
    setError(undefined)

    if (!selectedDates) {
      setData(undefined)
      setError('No data available.')
      setIsLoading(false)
      return
    }

    const fetchParams = {
      entity,
      entityId,
      kind,
      startDate: selectedDates[0],
      endDate: selectedDates[1],
      product: selectedProduct,
    } as TParams

    try {
      const response = await fetchFunction(fetchParams)

      if (!response.data) {
        setError('No data available.')
        return
      }

      setData(response.data)
    } catch (error) {
      console.error('Error fetching data:', error)
      setError('An error occurred while fetching data.')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (selectedDates) {
      fetchData()
    }
  }, [selectedDates, selectedProduct, ...dependencies])

  return { data, isLoading, error }
}

export default useFetchChartData
